import { Controller } from '@hotwired/stimulus'

const TIMER_DURATION = 5000

export default class extends Controller {
  static targets = ['button']
  static values = { id: String, url: String, param: { type: String, default: 'project_archiving[projects_ids][]' } }

  connect () {
    this.state = 'idle'
  }

  disconnect () {
    this.clearTimers()
  }

  toggle (event) {
    event.preventDefault()
    if (this.state === 'idle') {
      this.startPending()
    } else if (this.state === 'pending') {
      this.cancel()
    }
  }

  startPending () {
    this.state = 'pending'
    this.remainingSeconds = TIMER_DURATION / 1000
    this.renderPending()

    this.tickInterval = setInterval(() => {
      this.remainingSeconds--
      if (this.textEl) this.textEl.textContent = `Archived · ${this.remainingSeconds}s`
    }, 1000)

    this.timer = setTimeout(() => this.execute(), TIMER_DURATION)
  }

  renderPending () {
    const button = this.buttonTarget
    button.className = this.pendingClasses + ' relative overflow-hidden'
    button.innerHTML = ''

    const fill = document.createElement('span')
    fill.className = 'absolute inset-y-0 left-0 bg-blue-600'
    fill.style.width = '0%'
    fill.style.transition = `width ${TIMER_DURATION}ms linear`

    this.textEl = document.createElement('span')
    this.textEl.className = 'relative'
    this.textEl.textContent = `Archived · ${this.remainingSeconds}s`

    button.appendChild(fill)
    button.appendChild(this.textEl)

    fill.getBoundingClientRect()
    fill.style.width = '100%'
  }

  cancel () {
    this.clearTimers()
    this.state = 'idle'
    this.renderButton()
  }

  clearTimers () {
    if (this.timer) clearTimeout(this.timer)
    if (this.tickInterval) clearInterval(this.tickInterval)
    this.timer = null
    this.tickInterval = null
  }

  async execute () {
    this.clearTimers()
    this.state = 'archiving'
    this.renderButton()

    try {
      const csrfToken = document.querySelector('meta[name=csrf-token]')?.content
      const body = new URLSearchParams()
      body.append(this.paramValue, this.idValue)

      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'text/html',
          'X-CSRF-Token': csrfToken || ''
        },
        body: body.toString()
      })

      if (!response.ok) throw new Error('Archive failed')

      this.animateOut()
    } catch (e) {
      this.state = 'idle'
      this.renderButton()
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: { message: "Couldn't archive — try again", variant: 'error' }
      }))
    }
  }

  animateOut () {
    this.element.style.transition = 'opacity 250ms ease-out, transform 250ms ease-out, max-height 250ms ease-out, margin 250ms ease-out, padding 250ms ease-out'
    this.element.style.maxHeight = this.element.offsetHeight + 'px'
    requestAnimationFrame(() => {
      this.element.style.opacity = '0'
      this.element.style.transform = 'translateY(-8px)'
      this.element.style.maxHeight = '0px'
      this.element.style.marginTop = '0px'
      this.element.style.marginBottom = '0px'
      this.element.style.paddingTop = '0px'
      this.element.style.paddingBottom = '0px'
    })
    setTimeout(() => this.element.remove(), 300)
  }

  renderButton () {
    const button = this.buttonTarget
    if (this.state === 'idle') {
      button.textContent = 'Archive'
      button.className = this.idleClasses
    } else if (this.state === 'archiving') {
      button.textContent = 'Archiving…'
      button.className = this.pendingClasses
    }
  }

  get idleClasses () {
    return 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-50 hover:border-gray-300 transition shrink-0'
  }

  get pendingClasses () {
    return 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500 text-white text-xs font-semibold transition shrink-0'
  }
}
